// src/pages/AskAI.jsx
import { useState, useEffect, useRef } from "react";
import { Send, Sparkles, Plus } from "lucide-react";
import { db } from "../lib/firebase";
import {
  collection, addDoc, updateDoc, doc,
  Timestamp, onSnapshot, query, orderBy, limit
} from "firebase/firestore";
import { LoadingDots } from "../components/ui/primitives";
import { cn } from "../lib/utils";

const USER_ID = "gavin";

const SUGGESTIONS = [
  "Where am I wasting the most money?",
  "If I put $200 extra toward my Parent Plus loan, when does it pay off?",
  "Am I on track to grow my net worth this month?",
  "What did I spend on eating out vs. last month?",
  "How much have I spent on golf this year?",
  "What's my biggest spending category over the last 3 months?",
];

// Financial context snapshot passed to Claude with every message
const FINANCIAL_CONTEXT = `You are a personal finance AI assistant for Gavin, a 23-year-old fund accountant. You have full access to his financial data. Be direct, specific, and insightful — not generic. Always reference his actual numbers when relevant.

CURRENT FINANCIAL SNAPSHOT:
Net worth: ~$12,480
Assets: HHCU Checking $2,140 | HHCU Savings $8,420 | USAA Savings $3,200 | Cash $180 | Robinhood portfolio ~$14,280
Liabilities: USAA CC -$312 (19.9% APR) | Robinhood CC -$890 (24.99% APR) | Student loans -$28,400 total

STUDENT LOANS:
- Parent Plus Loan: $12,800 at 7.54% APR, ~$145/mo
- Subsidized Loan 1: $3,200 at 4.50% APR
- Subsidized Loan 2: $2,800 at 5.05% APR
- Subsidized Loan 3: $2,600 at 5.50% APR
- Unsubsidized Loan 4: $2,400 at 6.00% APR
- Unsubsidized Loan 5: $4,600 at 6.54% APR
Total: $303/mo, payoff est. May 2031

SPENDING (May so far):
Eating out: $380 (+34% vs April)
Sports betting: $290 (-12%)
Bars/alcohol: $287 (+18%)
Groceries: $180 (-8%)
Golf rounds: $137 (new)
Gas/fuel: $120 (+5%)
Books: $63 (-22%)
Subscriptions: $60

INCOME: $4,200 primary (Reynolds Construction payroll) + $180 alternative (dividends/gains) = $4,380 total
SPENDING: $3,190 | NET: +$1,190 | WEEKLY BURN: ~$798

INVESTMENTS (Robinhood):
NVDA: 14 shares, $6,300 FMV, +$1,120 (+21.6%)
AAPL: 22 shares, $3,960 FMV, +$340 (+9.4%)
SPY: 8 shares, $2,640 FMV, +$280 (+11.9%)
TSLA: 10 shares, $1,380 FMV, -$100 (-6.8%)

Cards are paid off weekly automatically. Answer questions conversationally and concisely. For "what if" scenarios, do the math and give a concrete answer.`;

async function callClaudeAI(messages) {
  const response = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      system: FINANCIAL_CONTEXT,
      messages: messages.map((m) => ({
        role: m.role,
        content: m.content,
      })),
    }),
  });

  const data = await response.json();
  return data.content?.[0]?.text || "I couldn't process that. Please try again.";
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-accent-green-muted border border-accent-green/20 flex items-center justify-center flex-shrink-0 mr-2 mt-1">
          <Sparkles size={12} className="text-accent-green" />
        </div>
      )}
      <div className={cn("max-w-[82%]", isUser ? "items-end" : "items-start", "flex flex-col")}>
        <div className={cn(
          "px-4 py-3 rounded-2xl text-[13px] leading-relaxed",
          isUser
            ? "bg-background-elevated border border-border text-text-primary rounded-br-sm"
            : "bg-background-secondary border border-border text-text-primary rounded-bl-sm"
        )}>
          {message.content}
        </div>
        <span className="text-[10px] text-text-tertiary mt-1 px-1 font-mono">
          {message.time || ""}
        </span>
      </div>
    </div>
  );
}

export function AskAI() {
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      content: "Hey Gavin — I have full context on your finances. Ask me anything about your spending, loans, investments, or savings. I can also run \"what if\" scenarios.",
      time: "Now",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  async function sendMessage(content) {
    if (!content.trim() || loading) return;

    const userMsg = {
      id: Date.now().toString(),
      role: "user",
      content: content.trim(),
      time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    // Build message history for Claude (exclude welcome message)
    const history = [...messages.filter((m) => m.id !== "welcome"), userMsg].map((m) => ({
      role: m.role,
      content: m.content,
    }));

    try {
      const aiResponse = await callClaudeAI(history);
      const aiMsg = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: aiResponse,
        time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
      };

      setMessages((prev) => [...prev, aiMsg]);

      // Persist to Firestore
      try {
        if (!conversationId) {
          const convRef = await addDoc(
            collection(db, "users", USER_ID, "aiConversations"),
            {
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
              preview: content.trim().slice(0, 60),
              messages: [userMsg, aiMsg].map((m) => ({
                role: m.role,
                content: m.content,
                time: m.time,
              })),
            }
          );
          setConversationId(convRef.id);
        } else {
          await updateDoc(
            doc(db, "users", USER_ID, "aiConversations", conversationId),
            {
              updatedAt: Timestamp.now(),
              messages: [...messages, userMsg, aiMsg]
                .filter((m) => m.id !== "welcome")
                .map((m) => ({ role: m.role, content: m.content, time: m.time })),
            }
          );
        }
      } catch (firestoreErr) {
        console.log("Firestore not connected yet — message not persisted");
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: "Something went wrong. Please try again.",
          time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  function startNewConversation() {
    setMessages([{
      id: "welcome",
      role: "assistant",
      content: "New conversation started. What would you like to know about your finances?",
      time: "Now",
    }]);
    setConversationId(null);
    setInput("");
  }

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 60px)" }}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3 flex justify-between items-center flex-shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-accent-green" />
          <h1 className="font-display text-lg font-bold text-text-primary">Ask AI</h1>
        </div>
        <button
          onClick={startNewConversation}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-xl text-[11px] text-text-secondary hover:text-text-primary transition-colors"
        >
          <Plus size={12} />
          New chat
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="w-7 h-7 rounded-full bg-accent-green-muted border border-accent-green/20 flex items-center justify-center flex-shrink-0 mr-2 mt-1">
              <Sparkles size={12} className="text-accent-green" />
            </div>
            <div className="bg-background-secondary border border-border rounded-2xl rounded-bl-sm px-4 py-3">
              <LoadingDots />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions (only when just the welcome message) */}
      {messages.length === 1 && (
        <div className="px-4 pb-2 flex-shrink-0">
          <p className="label mb-2">Suggested questions</p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.slice(0, 4).map((s) => (
              <button
                key={s}
                onClick={() => sendMessage(s)}
                className="text-[11px] px-3 py-1.5 border border-border rounded-full text-text-secondary hover:text-text-primary hover:border-border-strong transition-all bg-background-elevated"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-border bg-background-secondary/50 backdrop-blur-sm">
        <div className="flex items-end gap-2">
          <div className="flex-1 bg-background-elevated border border-border rounded-2xl px-4 py-3 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your finances..."
              rows={1}
              className="flex-1 bg-transparent text-[13px] text-text-primary placeholder:text-text-tertiary focus:outline-none resize-none leading-relaxed"
              style={{ maxHeight: "100px" }}
            />
          </div>
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            className={cn(
              "w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-200",
              input.trim() && !loading
                ? "bg-accent-green text-background shadow-glow"
                : "bg-background-elevated border border-border text-text-tertiary"
            )}
          >
            <Send size={15} />
          </button>
        </div>
        <p className="text-[10px] text-text-tertiary text-center mt-2">
          Conversations are saved · Enter to send
        </p>
      </div>
    </div>
  );
}
